import { forwardRef, useEffect, useState } from 'react';
import DeleteIcon from '@mui/icons-material/Delete';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button as MuiButton,
} from '@mui/material';
import { useFormik } from 'formik';
import * as yup from 'yup';
import { Candidate } from '@/services/candidates';
import Button from '@/trio-ui/Button';
import DropDown from '@/trio-ui/Dropdown';
import InputText from '@/trio-ui/InputText';
import TransparentButton from '@/trio-ui/TransparentButton';
import { Team, TeamArea, TeamCategory, User } from '@/types/api';
import { teamAreaOptions, teamCategoryOptions } from '@/utils/dropdownOptions';
import { requiredMessage } from '@/utils/formsValidation';
import * as Styles from './NewTeamFormModal.styles';
import AssignedUsers from '../AssignedUsers';
import { ImageUploaderValue } from '../UserInitialsInputFile/UserInitialsInputFile.component';
import { UserType } from '../UserSuggestions/UserSuggestions.component';

export const teamFormSchema = yup.object({
  area: yup.mixed<TeamArea>().oneOf(teamAreaOptions),
  category: yup.mixed<TeamCategory>().oneOf(teamCategoryOptions),
  name: yup.string().required(requiredMessage),
});

export type FormData = {
  area?: TeamArea;
  category?: TeamCategory;
  managerId?: string;
  memberIds?: string[];
  name: string;
  image?: File;
};

export type NewTeamFormModalComponentProps = {
  isOpen?: boolean;
  onClose?: () => void;
  onSubmit?: (data: FormData) => void;
  teamMemberSearch?: string;
  onTeamMembersSearch?: (value: string) => void;
  teamMembersFound?: Candidate[];
  managerSearch?: string;
  onManagerSearch?: (value: string) => void;
  managersFound?: Candidate[];
  isLoading?: boolean;
  editing?: Team;
  onDeleteClick: (teamId: string) => void;
  onSetMemberEndDate?: (profileId: string, endDate: string) => Promise<void>;
  setLockDrawer?: (value: boolean) => void;
};

const userTypeByUser = (user: User): UserType => {
  const [firstName, lastName] = (user.name || '').split(' ');

  return {
    id: user.personId || '',
    firstName,
    lastName,
    pictureUrl: user.person?.pictureUrl,
  };
};

const NewTeamFormModal = forwardRef<HTMLDivElement, NewTeamFormModalComponentProps>(
  (
    {
      isOpen = false,
      onClose,
      onSubmit,
      teamMemberSearch = '',
      teamMembersFound = [],
      onTeamMembersSearch,
      managerSearch = '',
      managersFound = [],
      onManagerSearch,
      isLoading = false,
      editing,
      onDeleteClick,
      onSetMemberEndDate,
      setLockDrawer,
    },
    ref
  ): JSX.Element => {
    const [teamMembers, setTeamMembers] = useState<UserType[]>([]);
    const [manager, setManager] = useState<UserType | undefined>();
    const [endDateMember, setEndDateMember] = useState<UserType | null>(null);
    const [endDateValue, setEndDateValue] = useState(new Date().toISOString().split('T')[0]);
    const [image, setImage] = useState<ImageUploaderValue>({
      imageUrl: '',
      imageFiles: [],
    });
    const [isUploaderOpen, setIsUploaderOpen] = useState<boolean>(false);

    useEffect(() => {
      setLockDrawer?.(isUploaderOpen);
    }, [isUploaderOpen, setLockDrawer]);

    const { handleSubmit, values, handleChange, errors, touched, resetForm, setFieldValue } =
      useFormik<yup.TypeOf<typeof teamFormSchema>>({
        initialValues: {
          name: '',
          area: undefined,
          category: undefined,
        },
        validateOnBlur: false,
        validationSchema: teamFormSchema,
        onSubmit: (data) => {
          if (isLoading) return;

          const formData = {
            name: data.name || '',
            area: data.area,
            category: data.category,
            managerId: manager?.id,
            memberIds:
              teamMembers.length > 0 ? teamMembers.map((member) => member.id || '') : undefined,
            image: image.imageFiles[0]?.file,
          } satisfies FormData;

          onSubmit?.(formData);
        },
      });

    useEffect(() => {
      if (isOpen) {
        resetForm();
        setFieldValue('name', editing?.name || '');
        setFieldValue('area', editing?.area || undefined);
        setFieldValue('category', editing?.category || undefined);
        setImage({
          imageUrl: '',
          imageFiles: [],
        });
        setManager(editing?.manager || undefined);

        const users = (editing?.members || []).map((teamMember) => teamMember.user);
        const filteredUsers: User[] = [];
        users.forEach((user) => {
          if (user) {
            filteredUsers.push(user);
          }
        });

        setTeamMembers(filteredUsers.map(userTypeByUser));
      }
    }, [isOpen, setFieldValue, editing, resetForm]);

    const handleSelectTeamMember = (userId: string): void => {
      const selectedUser = teamMembersFound.find((candidate) => candidate.id === userId);
      if (selectedUser) {
        setTeamMembers((arr) => [...arr, selectedUser]);
      }
    };

    const handleSelectManager = (userId: string): void => {
      const selectedUser = managersFound.find((candidate) => candidate.id === userId);

      if (selectedUser) {
        setManager(selectedUser);
      }
    };

    const handleRemoveUser = (userId: string): void => {
      if (editing && onSetMemberEndDate) {
        // When editing an existing team, show end date dialog instead of instant remove
        const member = teamMembers.find((m) => m.id === userId);
        if (member) {
          setEndDateMember(member);
          setEndDateValue(new Date().toISOString().split('T')[0]);
          return;
        }
      }
      // For new teams (not editing), just remove from local state
      setTeamMembers((arr) => arr.filter((member) => member.id !== userId));
    };

    const handleConfirmEndDate = async (): Promise<void> => {
      if (!endDateMember || !editing || !onSetMemberEndDate) return;
      await onSetMemberEndDate(endDateMember.id, endDateValue);
      setTeamMembers((arr) => arr.filter((member) => member.id !== endDateMember.id));
      setEndDateMember(null);
    };

    const [firstName, lastName] = (values.name || '').split(' ');

    return (
      <>
      <Dialog
        open={!!endDateMember}
        onClose={() => setEndDateMember(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 700, fontSize: 16 }}>
          Set End Date — {endDateMember?.firstName} {endDateMember?.lastName}
        </DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <TextField
            label="End Date"
            type="date"
            size="small"
            fullWidth
            value={endDateValue}
            onChange={(e) => setEndDateValue(e.target.value)}
            InputLabelProps={{ shrink: true }}
            helperText="When did this person leave the team?"
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <MuiButton
            onClick={() => setEndDateMember(null)}
            sx={{ textTransform: 'none', color: '#71717A' }}
          >
            Cancel
          </MuiButton>
          <MuiButton
            onClick={handleConfirmEndDate}
            variant="contained"
            sx={{ textTransform: 'none', backgroundColor: '#054AD9', '&:hover': { backgroundColor: '#043eaf' } }}
          >
            Confirm
          </MuiButton>
        </DialogActions>
      </Dialog>
      <Styles.Drawer $isOpen={isOpen} ref={ref}>
        <Styles.Header>
          <Styles.StyledUserInitials
            firstName={firstName || 'New'}
            lastName={values.name ? lastName : 'Team'}
            size={36}
            value={image}
            onChange={setImage}
            isOpen={isUploaderOpen}
            setIsOpen={setIsUploaderOpen}
            disableUrlInput
            initialPictureUrl={editing?.pictureUrl || undefined}
          />
          <Styles.HeaderTitle>{values.name || 'New Team'}</Styles.HeaderTitle>
        </Styles.Header>
        <Styles.Scrollable onSubmit={handleSubmit}>
          <InputText
            isLabelAboveField
            label="Team Name"
            name="name"
            placeholder="Lorem Ipsum"
            value={values.name}
            onChange={handleChange}
            errorMessage={(touched.name && errors.name) || undefined}
          />
          <DropDown
            isLabelAboveField
            label="Area"
            options={teamAreaOptions}
            name="area"
            defaultValue={values.area}
            errorMessage={(touched.area && errors.area) || undefined}
            onChangeDropDown={handleChange}
          />
          <DropDown
            isLabelAboveField
            label="Category"
            options={teamCategoryOptions}
            name="category"
            defaultValue={values.category}
            errorMessage={(touched.category && errors.category) || undefined}
            onChangeDropDown={handleChange}
          />
          <AssignedUsers
            label="Manager"
            allReviewers={managersFound}
            onAssign={handleSelectManager}
            assignedUsers={manager ? [manager] : []}
            onRemoveAssignedUser={() => setManager(undefined)}
            value={managerSearch}
            onSearch={onManagerSearch}
            placeholder="Search for a staff member"
          />
          <AssignedUsers
            label="Team Members"
            allReviewers={teamMembersFound}
            onAssign={handleSelectTeamMember}
            assignedUsers={teamMembers}
            onRemoveAssignedUser={handleRemoveUser}
            value={teamMemberSearch}
            onSearch={onTeamMembersSearch}
            placeholder="Search for a staff member"
          />
          {editing && (
            <div className="tw-border-t tw-pt-2.5 tw-border-neutral-40">
              <TransparentButton
                isRedButton
                icon={DeleteIcon}
                type="button"
                onClick={() => onDeleteClick(editing.id)}
              >
                Delete Team
              </TransparentButton>
            </div>
          )}
          <Styles.Footer>
            <Button type="submit" disabled={isLoading}>
              Save
            </Button>
            <Button type="button" variantStyle="outline" onClick={onClose}>
              Cancel
            </Button>
          </Styles.Footer>
        </Styles.Scrollable>
      </Styles.Drawer>
      </>
    );
  }
);

export default NewTeamFormModal;
